import { brandAndProductOf } from '@atlas/core-utils/classification'
import { AxisCode, type AxisSelection } from '@atlas/types'
import type { ComboboxOption } from '@atlas/ui-primitives'
import { EMPTY_MONEY_DRAFT } from '@/components/work-orders/MoneyField'
import { cleanDrafts, draftsFrom } from '@/components/work-orders/OriginalCodes'
import type { Campaign } from '@/domain/campaign.types'
import {
	type CreateWorkOrderInput,
	type WorkOrder,
	type WorkOrderLink,
	WorkOrderLinkSource,
} from '@/domain/work-order.types'
import type { RailAxis, RailValue } from '@/hooks/useLibrary'
import { geoOptions } from '@/lib/geo-options'
import { invalidMoneyMessage, parseMoney } from '@/lib/money'
import { MONEY_FIELDS, MONEY_LABELS, moneyDraftOf, moneyOf, originalCodesError } from './form-model.external'
import { type LockedFields, NO_LOCKED_FIELDS } from './form-model.locked'
import type { WorkOrderFormState } from './form-model.state'

export { ATLAS_NOTES_EMPTY, ATLAS_NOTES_HINT, ATLAS_NOTES_LABEL } from '@/components/work-orders/atlas-notes'
export {
	LOCKED_NOTE,
	type LockedFields,
	lockedFieldsOf,
	NO_LOCKED_FIELDS,
	WORKFRONT_CHILD_FIELDS,
	WORKFRONT_PARENT_FIELDS,
} from './form-model.locked'

import { sortedByLabel } from '@/lib/option-order'

export type { WorkOrderFormState } from './form-model.state'

export const EMPTY_FORM: WorkOrderFormState = {
	title: '',
	briefing: '',
	tuple: {},
	complexityLevel: '',
	assetTypeCode: '',
	durationSeconds: '',
	veevaCode: '',
	originalCodes: [],
	expectedPublishingDate: '',
	budget: EMPTY_MONEY_DRAFT,
	plannedCost: EMPTY_MONEY_DRAFT,
	finalCost: EMPTY_MONEY_DRAFT,
	campaignCode: '',
	dueAt: '',
	parentWorkOrderId: undefined,
	assigneeId: '',
	pmUserId: '',
	// Most work IS Content Factory work; the flag marks the exceptions.
	isContentFactory: true,
	links: [],
}

/**
 * The context axes fed through the facet engine.
 *
 * `product` is NOT one of them, and is not an axis: brand and product are two levels of
 * the single `brand` axis. The Product field is a second view of that one axis — the
 * children of the selected brand — so it is derived here rather than faceted (see
 * `productOptionsFor`). Listing a `product` axis here would make selecting a brand wipe
 * the rest of the form: the facet returns nothing for an axis that does not exist, and
 * the cascade reads that as "your selection is no longer valid".
 */
export const FACETED_CONTEXT_AXIS_CODES = ['geography', 'brand'] as const

/** The Context card's field labels. Shared so a message about a cleared field can name it. */
export const CONTEXT_LABELS: Record<string, string> = {
	brand: 'Brand',
	product: 'Product',
	geography: 'Region / Country',
	product_family: 'Product family',
	locale: 'Language',
	species: 'Species / Subspecies',
	audience: 'Audience',
	channel: 'Channel',
	business_unit: 'Business unit',
}

/** Where a brand sits on the merged axis; a product is anything below one. */
const BRAND_DEPTH = 0

/** The merged axis both the Brand and Product fields select from. */
export const BRAND_AXIS = 'brand'

/**
 * The Product field's key in form state. Deliberately NOT an axis code: it names a second
 * view of {@link BRAND_AXIS}, and `tupleFromForm` folds it back before anything is sent.
 */
export const PRODUCT_FIELD = 'product'

/**
 * The brands: the roots of the merged axis, never its families or products.
 *
 * Taken from the REGISTRY rather than the facet result. A facet answers "what has been
 * classified", which is the wrong question when creating a work order — it would offer
 * only combinations someone already used, and on a corpus where two objects carry a
 * geography that is almost nothing.
 */
export function brandOptionsFrom(axis: RailAxis | undefined): ComboboxOption[] {
	return (axis?.options ?? [])
		.filter((o) => o.depth === BRAND_DEPTH)
		.map((o) => ({ value: o.code, label: o.label, depth: 0 }))
}

/**
 * The products under one brand.
 *
 * Depth-relative to the brand, so they read flush however deep the brand sits. Empty
 * until a brand is chosen: every product with no brand to scope them is not a picker.
 */
export function productOptionsFor(axis: RailAxis | undefined, brandCode: string | undefined): ComboboxOption[] {
	if (!axis || !brandCode) return []
	const brand = axis.options.find((o) => o.code === brandCode)
	if (!brand) return []
	const inBrand: ComboboxOption[] = []
	// The options arrive in hierarchy order, so a brand's subtree is the run that
	// follows it until the next value at the brand's own depth or shallower.
	let started = false
	for (const option of axis.options) {
		if (option.code === brandCode) {
			started = true
			continue
		}
		if (!started) continue
		if (option.depth <= brand.depth) break
		inBrand.push({ value: option.code, label: option.label, depth: option.depth - brand.depth - 1 })
	}
	return inBrand
}

/**
 * Context axes that are independent of the geography/brand/product hierarchy: a
 * Bravecto/Germany order can still be produced in `pt-BR` for any species and
 * channel. Their options come straight from the cached bootstrap registry, so
 * they cost no facet request and a selection here is never auto-cleared.
 */
export const INDEPENDENT_CONTEXT_AXIS_CODES = [
	AxisCode.PRODUCT_FAMILY,
	AxisCode.LOCALE,
	AxisCode.SPECIES,
	AxisCode.AUDIENCE,
	AxisCode.CHANNEL,
	AxisCode.BUSINESS_UNIT,
] as const

/** Every axis the Context card binds, in display order. Campaign is not an axis — it's an entity picker. */
export const CONTEXT_AXIS_CODES = [...FACETED_CONTEXT_AXIS_CODES, ...INDEPENDENT_CONTEXT_AXIS_CODES] as const

/**
 * Rail axis → Combobox options (already active-filtered by the hook). `depth`
 * is carried through so hierarchical axes (species → subspecies, region →
 * country) render indented — e.g. "Companion Animal" with "Dog" nested under it.
 */
export function axisOptions(axis: RailAxis | undefined): ComboboxOption[] {
	if (!axis) return []
	return axis.options.map((o) => ({ value: o.code, label: o.label, depth: o.depth }))
}

/**
 * Faceted (narrowed) options for a context axis — the cascading comboboxes'
 * data source, from `useFacetedOptions`'s per-axis result rather than a static
 * `RailAxis`. Same option shape as `axisOptions`, just a different input.
 */
export function facetedAxisOptions(values: RailValue[] | undefined): ComboboxOption[] {
	// Geography carries its ISO code as a DESCRIPTION rather than a label prefix: the list
	// keeps reading as country names, and "ES" stays searchable beside "Spain". Every other
	// axis passes straight through — the helper only adds what a value actually holds.
	return geoOptions(values ?? [])
}

/**
 * Strip a leading `CODE ·`/`CODE -`/`CODE:` from a label that already carries its
 * own code, so prefixing never doubles it. Seeded complexity labels are authored
 * as "L1 · Minor edits", which would otherwise render "L1 · L1 · Minor edits".
 */
function labelWithoutCodePrefix(code: string, label: string): string {
	const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
	return label.replace(new RegExp(`^\\s*${escaped}\\s*[·:—–-]\\s*`, 'i'), '').trim() || label
}

/**
 * Coded-attribute options (complexity, asset-type): the picker shows
 * "CODE · label" (e.g. "D5 · Social video story / reel post") since the code is
 * the derived-display value designers recognise, and puts the label as the
 * searchable description second line. `value` stays the bare enum code.
 *
 * Ordered BY CODE, not by label: the code is the identity here, and its letter
 * groups the family (P = print, D = digital, V = video). Sorting on the label
 * scatters D1…D10 across the list under whatever noun each happens to start
 * with. Digits compare numerically so D2 precedes D10.
 */
export function codedOptions(axis: RailAxis | undefined): ComboboxOption[] {
	if (!axis) return []
	return axis.options
		.map((o) => {
			const label = labelWithoutCodePrefix(o.code, o.label)
			return { value: o.code, label: `${o.code} · ${label}`, description: label }
		})
		.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' }))
}

/** Campaign picker options — real Campaign entities, valued by their immutable code. */
export function campaignOptions(campaigns: Campaign[]): ComboboxOption[] {
	return sortedByLabel(campaigns.map((c) => ({ value: c.campaignCode, label: c.name, description: c.campaignCode })))
}

/** Look up a rail axis by code from the registry-loaded set. */
export function findAxis(axes: RailAxis[], code: string): RailAxis | undefined {
	return axes.find((a) => a.code === code)
}

/** datetime-local input value ('YYYY-MM-DDTHH:mm') → ISO datetime, or undefined. */
export function toIsoDateTime(local: string): string | undefined {
	if (!local) return undefined
	const d = new Date(local)
	return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

/** ISO datetime → local datetime-input value ('YYYY-MM-DDTHH:mm'), or '' if unset. */
export function toDateTimeLocal(iso: string | null): string {
	if (!iso) return ''
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return ''
	// Render in local time, trimmed to minutes (what <input type=datetime-local> wants).
	const pad = (n: number) => String(n).padStart(2, '0')
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Flatten the per-axis tuple state into an `AxisSelection` (single value per axis).
 *
 * `product` is a FIELD, not an axis — the Product picker is a second view of the merged
 * `brand` hierarchy — so it must not reach the wire under its own key. The classification
 * write resolves every key to an axis and THROWS on one it cannot find, so sending it
 * aborted the whole save: an order with a product chosen failed, the same order without
 * one succeeded, and the only feedback was a generic error naming no field.
 *
 * The two collapse to the MOST SPECIFIC value: a product is a descendant of its brand, so
 * tagging the product already implies the brand through the closure, and tagging both
 * would assert the brand twice.
 */
export function tupleFromForm(form: WorkOrderFormState): AxisSelection {
	const tuple: AxisSelection = {}
	for (const [key, code] of Object.entries(form.tuple)) {
		if (!code || key === PRODUCT_FIELD) continue
		tuple[key] = [code]
	}
	const mostSpecificBrand = form.tuple[PRODUCT_FIELD] || form.tuple[BRAND_AXIS]
	if (mostSpecificBrand) tuple[BRAND_AXIS] = [mostSpecificBrand]
	return tuple
}

/**
 * The selection the facet engine is asked about: the hierarchical axes only.
 * Passing the whole tuple would let an independent axis narrow brand/product —
 * the exact coupling this form is meant to be free of.
 */
export function facetedSelection(form: WorkOrderFormState): AxisSelection {
	const selection: AxisSelection = {}
	for (const axisCode of FACETED_CONTEXT_AXIS_CODES) {
		const code = form.tuple[axisCode]
		if (code) selection[axisCode] = [code]
	}
	return selection
}

/** Raw duration input → a positive integer, or undefined when blank/unparseable. */
export function parsedDuration(value: string): number | undefined {
	const trimmed = value.trim()
	if (!trimmed) return undefined
	const parsed = Number(trimmed)
	return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

/** Per-field validation messages, keyed by form field. Empty object = submittable. */
export type FormErrors = Partial<
	Record<
		| 'complexityLevel'
		| 'assetTypeCode'
		| 'durationSeconds'
		| 'title'
		| 'budget'
		| 'plannedCost'
		| 'finalCost'
		| 'originalCodes',
		string
	>
>

/**
 * The form's own copy of the rules `workOrderCreateSchema` enforces server-side.
 * Duration is never required — the format check only exists so a malformed entry
 * is reported rather than silently dropped on submit.
 *
 * A `locked` field is never judged: the person editing cannot change it here, so a gap in
 * it (a Workfront order still missing its size) must not hold the fields they can change.
 */
export function validateForm(form: WorkOrderFormState, locked: LockedFields = NO_LOCKED_FIELDS): FormErrors {
	const errors: FormErrors = {}
	const judged = (field: keyof WorkOrderFormState) => !locked.has(field)
	if (judged('title') && !form.title.trim()) errors.title = 'A title is required'
	if (judged('complexityLevel') && !form.complexityLevel) errors.complexityLevel = 'Complexity level is required'
	if (judged('assetTypeCode') && !form.assetTypeCode) errors.assetTypeCode = 'Type of asset is required'
	if (form.durationSeconds.trim() && parsedDuration(form.durationSeconds) === undefined)
		errors.durationSeconds = 'Duration must be a whole number of seconds'
	for (const field of MONEY_FIELDS) {
		if (judged(field) && parseMoney(form[field].amount, form[field].currency) === 'invalid')
			errors[field] = invalidMoneyMessage(MONEY_LABELS[field])
	}
	const codesError = judged('originalCodes') ? originalCodesError(form.originalCodes) : undefined
	if (codesError) errors.originalCodes = codesError
	return errors
}

/** Drop blank link rows. */
export function cleanLinks(links: WorkOrderLink[]): WorkOrderLink[] {
	return links.filter((l) => l.name.trim() && l.url.trim())
}

/** The rich-text editor emits '<p></p>' for empty — normalise that to no briefing. */
export function briefingHtml(html: string): string {
	const stripped = html.replace(/<p>\s*<\/p>/g, '').trim()
	return stripped
}

/** Assemble the create-work-order request body from flat form state. */
export function toCreateInput(form: WorkOrderFormState): CreateWorkOrderInput {
	return {
		title: form.title.trim(),
		briefing: briefingHtml(form.briefing) || undefined,
		tuple: tupleFromForm(form),
		complexityLevel: form.complexityLevel || undefined,
		assetTypeCode: form.assetTypeCode || undefined,
		durationSeconds: parsedDuration(form.durationSeconds),
		veevaCode: form.veevaCode.trim() || undefined,
		originalCodes: cleanDrafts(form.originalCodes),
		expectedPublishingDate: form.expectedPublishingDate || undefined,
		budget: moneyOf(form.budget) ?? undefined,
		plannedCost: moneyOf(form.plannedCost) ?? undefined,
		finalCost: moneyOf(form.finalCost) ?? undefined,
		campaignCode: form.campaignCode.trim() || undefined,
		dueAt: toIsoDateTime(form.dueAt),
		parentWorkOrderId: form.parentWorkOrderId,
		pmUserId: form.pmUserId.trim() || undefined,
		isContentFactory: form.isContentFactory,
		links: cleanLinks(form.links),
		assigneeIds: form.assigneeId.trim() ? [form.assigneeId.trim()] : [],
	}
}

/**
 * Hydrate flat form state from an existing work order (edit mode).
 *
 * `brandPath` is the order's `brand` tag with its ancestors (`brandPathOf`). The tag holds the
 * product when one was chosen, so it is split back into the two fields `tupleFromForm` folded
 * together — without it the Brand field would hold a product it cannot show, and Product
 * would be empty.
 */
export function fromWorkOrder(wo: WorkOrder, brandPath: string[] = []): WorkOrderFormState {
	const tuple: Record<string, string | undefined> = {}
	for (const [axisCode, codes] of Object.entries(wo.tuple)) tuple[axisCode] = codes[0]
	const { brandCode, productCode } = brandAndProductOf(brandPath)
	if (brandCode) tuple[BRAND_AXIS] = brandCode
	if (productCode) tuple[PRODUCT_FIELD] = productCode
	return {
		title: wo.title,
		briefing: wo.briefing ?? '',
		tuple,
		complexityLevel: wo.complexityLevel ?? '',
		assetTypeCode: wo.assetTypeCode ?? '',
		durationSeconds: wo.durationSeconds === null ? '' : String(wo.durationSeconds),
		veevaCode: wo.veevaCode ?? '',
		originalCodes: draftsFrom(wo.originalCodes),
		expectedPublishingDate: wo.expectedPublishingDate ?? '',
		budget: moneyDraftOf(wo.budget),
		plannedCost: moneyDraftOf(wo.plannedCost),
		finalCost: moneyDraftOf(wo.finalCost),
		campaignCode: wo.campaignCode ?? '',
		dueAt: toDateTimeLocal(wo.dueAt),
		parentWorkOrderId: wo.parentWorkOrderId ?? undefined,
		assigneeId: wo.assigneeIds[0] ?? '',
		pmUserId: wo.pmUserId ?? '',
		isContentFactory: wo.isContentFactory,
		links: manualLinksOf(wo),
	}
}

/** The links a person edits: the external system's own are mirrored by the sync and not in the form. */
function manualLinksOf(wo: WorkOrder): WorkOrderLink[] {
	return wo.links.filter((l) => l.source === WorkOrderLinkSource.MANUAL).map((l) => ({ name: l.name, url: l.url }))
}

/** Parent-order Combobox options — existing orders, labelled "WO-XXXX — title". */
export function parentOptions(orders: WorkOrder[]): ComboboxOption[] {
	return sortedByLabel(orders.map((w) => ({ value: w.workOrderId, label: `${w.orderCode} — ${w.title}` })))
}
